import { useState } from "react";
import {
	AddressWrapper,
	CommentWrapper,
	LegalNoticeContainer,
	OrderDetailsBrandLogo,
	OrderDetailsBrandLogoContainer,
	OrderDetailsColumnsContainer,
	OrderDetailsSummaryContainer
} from "components/OrderDetailsSummary/orderDetailsSummary.styles";
import {
	AccountNumberText,
	AckAccessoryCountText,
	AckCabinetCountText,
	AckCubeCountText,
	ItemsText,
	DesignerNumberText,
	LegalNoticeText,
	UnavailableDataPlaceholderText
} from "constants/text";
import BillToBox from "components/Common/BillToShipToBox/BillToBox/BillToBox";
import ShipToBox from "components/Common/BillToShipToBox/ShipToBox/ShipToBox";
import { models } from "types/api/viewModels.ts";
import { getOrderAccountInfo } from "pages/OrderPages/utils/OrderDetailUtil.ts";
import { getLegalContent } from "utils/legal.tsx";
import { Button } from "@mui/material";
import { brandCheck, displayDesigner, isMerchOrCampaignOrder } from "utils/order.ts";
import { useSelector } from "react-redux";
import { accountApiSlice } from "features/api/accountApi.ts";
import { AcknowledgementPriceFormat, OrderType } from "types/declarations/enums.ts";
import { checkForAndReplaceNullEmptyWhitespace } from "utils/string.ts";

const OrderDetailsSummary = ({ orderDetail, billTo }: Props) => {
	const [isReadMore, setIsReadMore] = useState(true);
	const accountInfo = useSelector(accountApiSlice.endpoints.getAccountsById.select(orderDetail?.orderId ?? "")).data;
	const orderAccount = getOrderAccountInfo(orderDetail, accountInfo);
	const parentBrandCode = orderDetail?.configurations?.[0]?.globals?.brand?.code;
	const isMerchOrder = orderDetail?.orderType === OrderType.MERCH;
	const usesMerchOrCampaignLayout = isMerchOrCampaignOrder(orderDetail?.orderType);
	const toggleReadMore = () => {
		setIsReadMore(!isReadMore);
	};

	return (
		<OrderDetailsSummaryContainer>
			{!usesMerchOrCampaignLayout && (
				<OrderDetailsBrandLogoContainer>
					<OrderDetailsBrandLogo
						data-testid="orderDetailsSummary-brandLogo"
						parentBrand={parentBrandCode}
						src={
							brandCheck(parentBrandCode)
								? `/assets/manufacture_logos/${parentBrandCode}.png`
								: "/assets/tandem_logos/cwg_logo.png"
						}
						alt={brandCheck(parentBrandCode) ? String(parentBrandCode) : "CabinetworksGroup Logo"}
					/>
				</OrderDetailsBrandLogoContainer>
			)}
			<OrderDetailsColumnsContainer isMerchOrder={usesMerchOrCampaignLayout}>
				<div data-testid="orderDetails-accountNumberColumn">
					<div>{AccountNumberText}</div>
					<div>{orderAccount?.accountDisplay ?? UnavailableDataPlaceholderText}</div>
				</div>
				{usesMerchOrCampaignLayout ? (
					<div data-testid="orderDetails-itemsCountColumn">
						<div>{ItemsText}</div>
						<div>{checkForAndReplaceNullEmptyWhitespace(orderDetail?.counts?.totalQuantityOrdered)}</div>
					</div>
				) : (
					<>
						<div>
							<div>{DesignerNumberText}</div>
							<div data-testid="orderDetailsHeader-Designer">{displayDesigner(orderDetail.designer)}</div>
						</div>
						<div data-testid="orderDetails-cabinetCountColumn">
							<div>{AckCabinetCountText}</div>
							<div>{orderDetail?.counts?.cabinetCount ?? UnavailableDataPlaceholderText}</div>
						</div>
						<div data-testid="orderDetails-accessoryCountColumn">
							<div>{AckAccessoryCountText}</div>
							<div>{orderDetail?.counts?.accessoryCount ?? UnavailableDataPlaceholderText}</div>
						</div>
						<div data-testid="orderDetails-cubeCountColumn">
							<div>{AckCubeCountText}</div>
							<div>{orderDetail?.counts?.cubeCount ?? UnavailableDataPlaceholderText}</div>
						</div>
					</>
				)}
			</OrderDetailsColumnsContainer>
			<AddressWrapper>
				<ShipToBox
					orderDetail={orderDetail}
					dataTestIdPrefix="orderDetails"
				/>
				<BillToBox
					billTo={billTo}
					orderDetail={orderDetail}
					orderType={orderDetail.orderType}
					isNoChargeOrder={orderDetail.isNoChargeOrder}
					orderDetailSubTotal={orderDetail.pricing?.listPrice}
					orderDetailNetTotal={orderDetail.pricing?.finalNetPrice}
					ackPriceFormatOverride={isMerchOrder ? AcknowledgementPriceFormat.LIST : undefined}
					hasBorder
					hasMinHeight
					dataTestIdPrefix="orderDetails"
				/>
			</AddressWrapper>
			{orderDetail.labelComments && orderDetail?.labelComments?.length > 1 && (
				<CommentWrapper>
					<p
						className="body2"
						data-testid="orderDetails-orderComments"
					>
						{orderDetail?.labelComments?.length > 1 ? <b className="subtitle2">Comment: </b> : null}
						{isReadMore ? orderDetail?.labelComments?.slice(0, 250) : orderDetail?.labelComments}
						{orderDetail?.labelComments?.length > 250 && (
							<Button
								variant="text"
								sx={{ padding: 0 }}
								onClick={toggleReadMore}
								onKeyDown={toggleReadMore}
								data-testid="orderDetails-commentViewMoreViewLess"
							>
								{isReadMore ? "...View More" : "...View Less"}
							</Button>
						)}
					</p>
				</CommentWrapper>
			)}

			{orderDetail.isAcknowledgementEligible &&
				orderDetail.legalNotice?.content &&
				!orderDetail.legalNotice?.warningTitle && (
					<LegalNoticeContainer>
						<span>{LegalNoticeText}</span>
						<span>
							{getLegalContent(orderDetail.legalNotice?.content, orderDetail.legalNotice?.placeholders)}
						</span>
					</LegalNoticeContainer>
				)}
		</OrderDetailsSummaryContainer>
	);
};

interface Props {
	orderDetail: models["OrderDetailViewModel"];
	billTo: models["BillToViewModel"] | null;
}

export default OrderDetailsSummary;
